import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { base44 } from "@voult/api/base44Client";
import { CreditCard, Plus, Search, Loader2 } from "lucide-react";

const BRAND_COLORS = {
  visa: "bg-blue-100 text-blue-700",
  mastercard: "bg-orange-100 text-orange-700",
  amex: "bg-green-100 text-green-700",
  discover: "bg-yellow-100 text-yellow-700",
  unknown: "bg-slate-100 text-slate-600",
};

const THEME_PREVIEWS = {
  midnight: "from-slate-900 via-indigo-950 to-slate-900",
  emerald: "from-emerald-900 via-teal-900 to-slate-900",
  sunset: "from-rose-900 via-orange-900 to-slate-900",
  royal: "from-violet-900 via-purple-950 to-slate-900",
  graphite: "from-zinc-800 via-zinc-900 to-black",
};

export default function VaultList() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    base44.entities.Card.list("-created_date", 100).then(setCards).finally(() => setLoading(false));
  }, []);

  const filtered = cards.filter((c) =>
    [c.cardholder_name, c.nickname, c.last4, c.brand].some((v) =>
      v?.toLowerCase().includes(search.toLowerCase())
    )
  );

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">My Vault</h1>
          <p className="text-sm text-slate-500 mt-0.5">{cards.length} tokenized card{cards.length !== 1 ? "s" : ""}</p>
        </div>
        <Link
          to="/voult/add-card"
          className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-800 transition"
        >
          <Plus className="h-4 w-4" /> Add card
        </Link>
      </div>

      <div className="relative mb-5">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, brand, or last 4…"
          className="w-full rounded-xl border border-slate-200 bg-white pl-9 pr-4 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-900 focus:ring-2 focus:ring-slate-900/5"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-slate-500">
          <CreditCard className="h-10 w-10 mx-auto mb-3 text-slate-300" />
          <p className="font-medium">{search ? "No cards match your search" : "No cards in vault yet"}</p>
          {!search && <Link to="/voult/add-card" className="text-sm text-indigo-600 hover:underline mt-1 inline-block">Add your first card →</Link>}
        </div>
      ) : (
        <div className="grid gap-4">
          {filtered.map((card) => (
            <Link
              key={card.id}
              data-testid="voult-card-row"
              to={`/voult/token-details?id=${card.id}`}
              className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-4 hover:border-slate-300 hover:shadow-sm transition group"
            >
              <div className={`h-12 w-20 flex-shrink-0 rounded-lg bg-gradient-to-br ${THEME_PREVIEWS[card.color_theme] || THEME_PREVIEWS.midnight} flex items-center justify-center`}>
                <CreditCard className="h-5 w-5 text-white/60" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-900 truncate">{card.nickname || card.cardholder_name}</p>
                <p className="text-sm text-slate-500 font-mono">•••• •••• •••• {card.last4}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold uppercase ${BRAND_COLORS[card.brand] || BRAND_COLORS.unknown}`}>
                  {card.brand}
                </span>
                <span className="text-xs text-slate-400">{card.expiry_month}/{card.expiry_year}</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}